import { useEffect } from 'react';

import { FeatureFlagProfile } from 'pages/account/feature_flag/components/FeatureFlagProfile/FeatureFlagProfile';
import { FeatureFlagApi, FeatureFlag as FeatureFlagType } from 'apis/FeatureFlagApi';
import { IntlProvider, LocalizationProvider } from '@progress/kendo-react-intl';
import { EarlyAccessRing, EarlyAccessRingApi } from 'apis/EarlyAccessRingApi';
import { useFeatureFlagDetails } from 'pages/account/feature_flag/context';
import { GMetrixLoader } from 'components/GMetrixLoader/GMetrixLoader';
import { helpContent } from 'pages/account/feature_flag/help';
import { useNavigate, useParams } from 'react-router-dom';
import { useNotification } from 'app/notifications';
import { PageHeader } from 'components/layout/page';
import { KendoIntlProvider } from 'app/culture';
import { Container } from 'react-bootstrap';
import { useSuperState } from 'hooks';
import { useIntl } from 'react-intl';

import 'pages/account/feature_flag/styles/main.scss';
import 'styles/global-styles.scss';

// The "In Development" early access ring a brand-new feature flag is seeded with.
const IN_DEVELOPMENT_RING_ID = 35;

export const FeatureFlagDetail = () => {

    const { id } = useParams();
    const isCreate = id === 'new';
    const flagId = isCreate ? 0 : Number(id);
    const navigate = useNavigate();

    const [state, setState] = useSuperState({
        earlyAccessRings: [] as EarlyAccessRing[],
        loading: true
    });

    const { earlyAccessRings, loading } = state;

    const { open, pageContext, updateById } = useFeatureFlagDetails();
    const { formatMessage, locale } = useIntl();
    const { catchWithToast, createToast } = useNotification();

    const loadFlag = async () => {

        try {

            setState({ loading: true });

            const rings = await EarlyAccessRingApi.GetAll().catch(catchWithToast(formatMessage({ id: 'failed_to_load_access_rings.b16178e9', defaultMessage: 'Failed to load access rings.' })));
            setState({ earlyAccessRings: rings ?? [] });

            if (isCreate) {

                const inDevelopmentAccessRing = (rings ?? []).find(accessRing => accessRing.Id === IN_DEVELOPMENT_RING_ID);

                if (!inDevelopmentAccessRing) {

                    createToast(formatMessage({ id: 'unable_to_start_a_new_feature_flag.0e3adac7', defaultMessage: 'Unable to start a new feature flag.' }), 'danger');
                    setState({ loading: false });
                    return;

                }

                open({
                    Data: {
                        featureFlagDetails: {
                            Changes: [],
                            CreateDate: '',
                            CurrentRingName: '',
                            Description: '',
                            DisplayName: '',
                            EarlyAccessRing: inDevelopmentAccessRing,
                            Id: 0,
                            Name: '',
                            ReleaseNotes: ''
                        }
                    },
                    Id: 0,
                    Title: formatMessage({ id: 'new_feature_flag.cea7185a', defaultMessage: 'New Feature Flag' })
                });

            } else {

                const featureFlagDetails = await FeatureFlagApi.Get(flagId).catch(catchWithToast(formatMessage({ id: 'failed_to_load_feature_flag_details.8668c39d', defaultMessage: 'Failed to load feature flag details.' })));

                if (!featureFlagDetails) {

                    createToast(formatMessage({ id: 'feature_flag_not_found.4b508273', defaultMessage: 'Feature flag not found.' }), 'danger');
                    setState({ loading: false });
                    return;

                }

                open({
                    Data: { featureFlagDetails },
                    Id: featureFlagDetails.Id,
                    Title: featureFlagDetails.Name
                });

            }

        } catch (e) {

            console.error(e);
            createToast(formatMessage({ id: 'failed_to_load_feature_flag_details_plea.a6762247', defaultMessage: 'Failed to load feature flag details. Please try again.' }), 'danger');

        } finally {

            setState({ loading: false });

        }

    };

    // Called by the profile after a create (navigate to the new flag's detail) or after an
    // update (re-pull the flag so the saved changes are reflected).
    const onDetailsItemClick = async (dataItem: FeatureFlagType, isNewFeatureFlag = false, updateGlobalState = false) => {

        if (isNewFeatureFlag) {

            navigate('/features/manage/' + dataItem.Id);
            return;

        }

        if (updateGlobalState) {

            const fresh = await FeatureFlagApi.Get(dataItem.Id).catch(catchWithToast(formatMessage({ id: 'failed_to_reload_feature_flag.b61e4439', defaultMessage: 'Failed to reload feature flag.' })));
            if (fresh) {

                updateById(dataItem.Id, { featureFlagDetails: fresh });

            }

        }

    };

    const onFeatureFlagNotification = () => {

        createToast(isCreate ? formatMessage({ id: 'feature_flag_created_successfully.1361d0f8', defaultMessage: 'Feature Flag created successfully.' }) : formatMessage({ id: 'feature_flag_updated_successfully.c21471b8', defaultMessage: 'Feature Flag updated successfully.' }), 'success');

    };

    useEffect(() => {

        loadFlag();

    }, [id]);

    return (
        <KendoIntlProvider>
            <LocalizationProvider language={locale}>
                <IntlProvider locale={locale}>
                    <div className="tab-page-container">
                        <PageHeader
                            helpContent={helpContent}
                            title={pageContext?.Title ?? ''}
                            trail={[{ label: formatMessage({ id: 'manage_feature_flags.9637e309', defaultMessage: 'Manage Feature Flags' }), to: '/features/manage' }]}
                        />
                        {loading || !pageContext ? (
                            <Container fluid className="centerPage">
                                <GMetrixLoader size={'large'} />
                            </Container>
                        ) : (
                            <Container fluid className="px-5 pb-4">
                                <div className="card card-body">
                                    <FeatureFlagProfile
                                        earlyAccessRings={earlyAccessRings}
                                        mode={isCreate ? 'Create' : 'Edit'}
                                        onDetailsItemClick={onDetailsItemClick}
                                        onFeatureFlagNotification={onFeatureFlagNotification}
                                    />
                                </div>
                            </Container>
                        )}
                    </div>
                </IntlProvider>
            </LocalizationProvider>
        </KendoIntlProvider>
    );

};
